/**
 * Notification Badge Component
 *
 * Bell icon button with unread notification count badge.
 * Opens notification inbox dropdown when clicked.
 */

import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWatchZones } from '../contexts/WatchZoneContext';
import NotificationInbox from './NotificationInbox';
import './NotificationBadge.css';
import { isTouchPhoneNow } from '../hooks/useIsTouchPhone';

interface NotificationBadgeProps {
  onLoginClick?: () => void;
  onViewAll?: () => void;
  onEventSelect?: (eventId: string) => void;
}

export default function NotificationBadge({ onLoginClick, onViewAll, onEventSelect }: NotificationBadgeProps) {
  const { isAuthenticated } = useAuth();
  const { unreadCount, refreshUnreadCount } = useWatchZones();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Refresh unread count periodically when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      refreshUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, refreshUnreadCount]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle button click. On desktop the bell opens an inline dropdown
  // (Inbox); on mobile we skip the dropdown and go straight to the
  // full-screen NotificationPage — dropdowns are awkward on small screens
  // and we already have a richer page-level view for the same data.
  const handleClick = () => {
    if (!isAuthenticated) {
      onLoginClick?.();
      return;
    }
    const isMobile = isTouchPhoneNow();
    if (isMobile) {
      onViewAll?.();
      return;
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="notification-badge-container" ref={containerRef}>
      <button
        className={`notification-badge-btn ${isOpen ? 'active' : ''}`}
        onClick={handleClick}
        title={isAuthenticated ? 'Notifications' : 'Sign in for notifications'}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <svg
          className="notification-badge-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {isAuthenticated && unreadCount > 0 && (
          <span className="notification-badge-count">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && isAuthenticated && (
        <NotificationInbox
          onClose={() => setIsOpen(false)}
          onViewAll={() => {
            setIsOpen(false);
            onViewAll?.();
          }}
          onEventSelect={onEventSelect ? (eventId: string) => {
            setIsOpen(false);
            onEventSelect(eventId);
          } : undefined}
        />
      )}
    </div>
  );
}
